import supabase from '../services/supabase';
import { sendNotification } from './notificationService';

/**
 * Add a comment to a post
 * @param {Object} commentData - Comment data including post_id, user_id, content
 * @returns {Promise<Object>} - The newly created comment
 */
export const addComment = async (commentData) => {
  try {
    // Insert the comment
    const { data, error } = await supabase
      .from('post_comments')
      .insert([commentData])
      .select(`
        *,
        user_profiles!post_comments_user_id_fkey(
          user_id,
          username,
          profilePicture
        )
      `)
      .single();
      
    if (error) throw error;
    
    // Update post_stats with new comment count
    const { data: commentCount, error: countError } = await supabase
      .from('post_comments')
      .select('id', { count: 'exact' })
      .eq('post_id', commentData.post_id);
    
    if (countError) throw countError;
    
    console.log(`Updating post_stats for post ${commentData.post_id} with ${commentCount.length} comments`);
    
    // Update post_stats table
    const { data: updateData, error: updateError } = await supabase
      .from('post_stats')
      .update({ 
        comments_count: commentCount.length,
        updated_at: new Date().toISOString()
      })
      .eq('post_id', commentData.post_id)
      .select();
    
    if (updateError) {
      console.error("Error updating post_stats:", updateError);
      throw updateError;
    }
    
    console.log("Successfully updated post_stats:", updateData);
    
    // Notify post owner (non-blocking)
    try {
      const { data: postRow } = await supabase
        .from('posts')
        .select('user_id')
        .eq('id', commentData.post_id)
        .single();
      const { data: { user } } = await supabase.auth.getUser();
      if (postRow?.user_id && user?.id) {
        await sendNotification({
          recipientId: postRow.user_id,
          actorId: user.id,
          type: 'post_comment',
          postId: commentData.post_id
        });
      }
    } catch (_) {}
    console.log("Returning comment data:", data);
    
    // Transform the data to match expected structure
    const transformedData = {
      ...data,
      user: {
        user_id: data.user_id,
        username: data.user_profiles?.username || data.username,
        profilePicture: data.user_profiles?.profilePicture || data.profilePicture
      }
    };
    
    console.log("Transformed comment data:", transformedData);
    return transformedData;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

/**
 * Delete a comment by ID
 * @param {string} commentId - ID of the comment to delete
 * @returns {Promise<void>}
 */
export const deleteComment = async (commentId) => {
  try {
    // First get the post_id before deleting the comment
    const { data: comment, error: fetchError } = await supabase
      .from('post_comments')
      .select('post_id')
      .eq('id', commentId)
      .single();
    
    if (fetchError) throw fetchError;
    
    // Delete the comment
    const { error } = await supabase
      .from('post_comments')
      .delete()
      .eq('id', commentId);
      
    if (error) throw error;
    
    // Update post_stats with new comment count
    const { data: commentCount, error: countError } = await supabase
      .from('post_comments')
      .select('id', { count: 'exact' })
      .eq('post_id', comment.post_id);
    
    if (countError) throw countError;
    
    // Update post_stats table
    const { error: updateError } = await supabase
      .from('post_stats')
      .update({ 
        comments_count: commentCount.length,
        updated_at: new Date().toISOString()
      })
      .eq('post_id', comment.post_id);
    
    if (updateError) throw updateError;
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

/**
 * Edit a comment by ID
 * @param {string} commentId - ID of the comment to edit
 * @param {string} newContent - The new content for the comment
 * @returns {Promise<Object>} - The updated comment
 */
export const editComment = async (commentId, newContent) => {
  try {
    const { data, error } = await supabase
      .from('post_comments')
      .update({ content: newContent })
      .eq('id', commentId)
      .select(`
        *,
        user_profiles!post_comments_user_id_fkey(
          user_id,
          username,
          profilePicture
        )
      `)
      .single();
    if (error) throw error;
    
    // Transform the data to match expected structure
    const transformedData = {
      ...data,
      user: {
        user_id: data.user_id,
        username: data.user_profiles?.username || data.username,
        profilePicture: data.user_profiles?.profilePicture || data.profilePicture
      }
    };
    
    return transformedData;
  } catch (error) {
    console.error('Error editing comment:', error);
    throw error;
  }
};

/**
 * Get comments for a post
 * @param {string} postId - ID of the post to get comments for
 * @returns {Promise<Array>} - Array of comments
 */
export const getComments = async (postId) => {
  try {
    const { data, error } = await supabase
      .from('post_comments')
      .select(`
        *,
        user_profiles!post_comments_user_id_fkey(
          user_id,
          username,
          profilePicture
        )
      `)
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
      
    if (error) throw error;
    
    // Transform the data to match expected structure
    const transformedData = (data || []).map(comment => ({
      ...comment,
      user: {
        user_id: comment.user_id,
        username: comment.user_profiles?.username || comment.username,
        profilePicture: comment.user_profiles?.profilePicture || comment.profilePicture
      }
    }));
    
    return transformedData;
  } catch (error) {
    console.error('Error fetching comments:', error);
    throw error;
  }
}; 
